import { Tip } from '~/components/ui/tip';
import type { ContextUsage } from '~/features/coding/coding-events';

/**
 * Compact abbreviation for a raw token count in the unknown-window chip
 * (e.g. `217888` -> `"218k"`, `1234567` -> `"1.2m"`). Not locale-aware — this
 * is a terse label next to a chip, not a number meant to be read exactly;
 * the exact count is still in the tooltip via `toLocaleString()`.
 */
function formatCompactTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n}`;
}

/**
 * FRONT-2258: the meter's fill ramps blue -> violet -> red as the window
 * fills, so a glance at the colour says as much as reading the number. Mixed
 * from the app's own tokens rather than literal hex, so it follows the theme.
 */
function contextFill(percent: number): string {
  const t = Math.min(100, Math.max(0, percent));
  if (t <= 50) return `color-mix(in oklab, var(--eddy-violet) ${t * 2}%, var(--eddy-blue))`;
  return `color-mix(in oklab, var(--destructive) ${(t - 50) * 2}%, var(--eddy-violet))`;
}

/**
 * Header context-size meter (FRONT-645). Shows roughly how full the coding
 * model's context window is, from the SDK usage on the last finished run. It
 * is a post-hoc estimate (updates per run, not live; can fall after the SDK
 * compacts) — the tooltip says so. Hidden until a run with usage has landed.
 *
 * FRONT-1651: the true percentage can run past 100 (the window can be wrong,
 * or a run can genuinely overflow it) and the tooltip/label say so honestly —
 * only the BAR's fill still clamps, since a fill can't visually exceed its
 * own track.
 *
 * An older tape event (every one written before this change) has no known
 * window. That state renders NO bar track at all, rather than an EMPTY one —
 * an empty-looking bar next to no number reads as "barely any context used,"
 * which on deploy day would be true for literally every existing session
 * regardless of its real size, a worse lie than the clamped-100% bug this
 * whole change fixes. Instead the raw token count itself goes on screen
 * (e.g. `218k`), not just in the tooltip.
 *
 * The word "Context" is a standing LABEL now rather than a suffix on the
 * value ("22% context"), so the readout reads left-to-right as a named
 * measurement. The whole thing carries a test id (the guided Builder's
 * `coding-context-meter` by default): its rendered words legitimately differ
 * between the known- and unknown-window branches, so a browser spec that
 * keyed on either sentence went red on a restyle that changed nothing it
 * existed to guard.
 *
 * FRONT-3077: moved here from the guided Builder's window so Pro Builder's
 * header renders the same meter (fed from its own session read, with its own
 * `testId`). Two callers now: `coding-session-modal.tsx` and
 * `pro-builder-window.tsx`.
 */
export function ContextMeter({ usage, testId = 'coding-context-meter' }: { usage: ContextUsage | null; testId?: string }) {
  if (!usage) return null;
  const { contextTokens, windowTokens, percent } = usage;
  const windowKnown = windowTokens != null && percent != null;
  const tooltip = windowKnown
    ? `About ${percent}% of the coding model's context window in use — roughly ${contextTokens.toLocaleString()} of ${windowTokens.toLocaleString()} tokens, measured after the last run.`
    : `Roughly ${contextTokens.toLocaleString()} tokens used, measured after the last run. The coding model's context window isn't known for this run.`;
  return (
    <Tip label={tooltip}>
      <span
        data-testid={testId}
        className="hidden sm:inline-flex items-center gap-2.5 h-[34px] shrink-0 rounded-[10px] px-3 text-[length:var(--text-sm)] font-medium text-muted-foreground whitespace-nowrap hover:bg-muted transition-colors cursor-default"
      >
        Context
        {windowKnown ? (
          <>
            <span
              className="relative h-1 w-14 rounded-full bg-muted-foreground/20 overflow-hidden"
              aria-hidden
            >
              {/* A 2% floor so a barely-used window still shows a sliver of
                  fill — an empty track reads as a broken bar. The fill still
                  CLAMPS at 100 while the label and tooltip report the honest
                  number, which can exceed it (FRONT-1651). */}
              <span
                className="absolute inset-y-0 left-0 rounded-full transition-[width,background-color] duration-300"
                style={{
                  width: `${Math.min(100, Math.max(2, percent))}%`,
                  background: contextFill(percent),
                }}
              />
            </span>
            <span className="tabular-nums text-foreground min-w-[32px] text-right">{`${percent}%`}</span>
          </>
        ) : (
          <span className="tabular-nums text-foreground">{formatCompactTokenCount(contextTokens)}</span>
        )}
      </span>
    </Tip>
  );
}
